/**
 * ADOBE CONFIDENTIAL
 * __________________
 * Copyright 2023 Adobe
 * All Rights Reserved.
 * __________________
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 */

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes, useState } from 'preact/compat';
import { classes, VComponent } from '@adobe-commerce/elsie/lib';
import { useText } from '@adobe-commerce/elsie/i18n';

import { Carousel, GalleryGrid, Overlay, Zoom } from '@/pdp/components';
import '@/pdp/components/Product/Product.css';

import { CarouselConfig } from '@/pdp/containers';

interface ProductCarouselConfig extends CarouselConfig {
  thumbnails: VNode[];
}

export interface ProductProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: VNode;
  breadcrumbs?: VNode;
  galleryContent?: VNode;
  infoContent?: VNode;
  productContent?: VNode;
  shortDescription?: VNode;
  description?: VNode;
  attributes?: VNode;
  images: VNode[];
  options?: VNode;
  giftCardOptions?: VNode;
  downloadableOptions?: VNode;
  sku?: VNode;
  price?: VNode;
  specialPrice?: VNode;
  outOfStock: boolean;
  actions?: VNode;
  quantity?: VNode;
  carouselConfig?: ProductCarouselConfig;
  zoomType?: 'zoom' | 'overlay';
  closeButton?: boolean;
}

export const Product: FunctionComponent<ProductProps> = ({
  title,
  breadcrumbs,
  galleryContent,
  infoContent,
  productContent,
  sku,
  outOfStock,
  shortDescription,
  description,
  attributes,
  images,
  options,
  giftCardOptions,
  downloadableOptions,
  price,
  specialPrice,
  className,
  children,
  quantity,
  actions,
  carouselConfig,
  zoomType = 'overlay',
  closeButton = true,
  ...props
}) => {
  // i18n
  const RegularPriceLabel = useText('PDP.Product.RegularPrice.label').label;
  const SpecialPriceLabel = useText('PDP.Product.SpecialPrice.label').label;
  const OutOfStockLabel = useText('PDP.Product.OutOfStock.label').label;
  const ImagesLabel = useText('PDP.Product.Image.label').label;

  const [zoom, setZoom] = useState<number | null>(null);

  const galleryImages = images?.length ? (
    images.map((image, key) => {
      const alt =
        title?.props?.children &&
        ImagesLabel?.replace('{product}', title.props.children.toString())
          .replace('{key}', String(key + 1))
          .replace('{total}', String(images.length));
      return zoomType === 'zoom' ? (
        <Zoom closeButton={closeButton}>
          <VComponent
            key={key}
            node={image}
            loading={key === 0 ? 'eager' : 'lazy'}
            alt={alt}
          />
        </Zoom>
      ) : (
        <VComponent
          key={key}
          node={image}
          loading={key === 0 ? 'eager' : 'lazy'}
          alt={alt}
          onClick={() => setZoom(key)}
        />
      );
    })
  ) : (
    <img
      src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
      className="pdp-product__images__placeholder"
      aria-hidden
    />
  );

  const localThumbnailsImages = carouselConfig?.thumbnails?.length ? (
    carouselConfig.thumbnails.map((image, key) => (
      <VComponent
        key={key}
        node={image}
        loading={carouselConfig?.thumbnailsLoadingMode || 'lazy'}
        alt={
          title?.props?.children &&
          ImagesLabel?.replace('{product}', title.props.children.toString())
            .replace('{key}', String(key + 1))
            .replace('{total}', String(images.length))
        }
      />
    ))
  ) : (
    <img
      src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
      className="pdp-product__images__placeholder"
      aria-hidden
    />
  );

  return (
    <div {...props} className={classes(['pdp-product', className])}>
      {breadcrumbs && (
        <VComponent node={breadcrumbs} className="pdp-product__breadcrumbs" />
      )}
      <div className="pdp-product__column-container pdp-product__gallery-column">
        <div className="pdp-product__column-body">
          {carouselConfig?.controls?.desktop ? (
            <Carousel
              className="pdp-product__overlay__carousel"
              arrows={images?.length > 1}
              controls={
                images?.length > 1 ? carouselConfig.controls.desktop : 'dots'
              }
              arrowsOnMainImage={carouselConfig?.arrowsOnMainImage}
              loop={carouselConfig?.loopable && images?.length > 1}
              peak={
                (carouselConfig?.thumbnails?.length > 1 &&
                  carouselConfig?.peak?.desktop) ||
                false
              }
              gap={carouselConfig?.gap || null}
              width="100%"
              height="100%"
              defaultIndex={zoom || 0}
              thumbnails={localThumbnailsImages}
            >
              {galleryImages}
            </Carousel>
          ) : (
            <GalleryGrid className="pdp-product__images" gap="small">
              {galleryImages}
            </GalleryGrid>
          )}
        </div>
        {galleryContent && (
          <VComponent
            node={galleryContent}
            className="pdp-product__gallery-content"
          />
        )}
      </div>

      <div className="pdp-product__column-container pdp-product__content-column">
        <div className="pdp-product__column-body">
          <div className="pdp-product__header">
            {title && (
              <VComponent node={title} className="pdp-product__title" />
            )}

            {sku && <VComponent node={sku} className="pdp-product__sku" />}
          </div>

          <div className="pdp-product__prices">
            {specialPrice && (
              <VComponent
                node={specialPrice}
                className={classes([
                  'pdp-product__price-special',
                  'pdp-product__price',
                ])}
                aria-label={SpecialPriceLabel}
                role="text"
              />
            )}
            {price && (
              <VComponent
                node={price}
                className={classes([
                  'pdp-product__price-regular',
                  'pdp-product__price--grey',
                ])}
                aria-label={RegularPriceLabel}
                role="text"
              />
            )}
          </div>

          {/* Small Screen Gallery */}
          {carouselConfig?.controls?.mobile &&
          carouselConfig?.controls?.mobile === 'thumbnailsRow' ? (
            <Carousel
              className="pdp-product__images pdp-product__images--carousel pdp-product__images--carousel--thumbnails"
              width="100%"
              height="auto"
              arrows={images?.length > 1}
              controls={images?.length > 1 ? 'thumbnailsRow' : null}
              loop={carouselConfig?.loopable && images?.length > 1}
              peak={
                (carouselConfig?.thumbnails?.length > 1 &&
                  carouselConfig?.peak?.mobile) ||
                false
              }
              gap={carouselConfig?.gap || null}
              thumbnails={localThumbnailsImages}
            >
              {galleryImages}
            </Carousel>
          ) : (
            <Carousel
              className="pdp-product__images pdp-product__images--carousel"
              peak={carouselConfig?.peak?.mobile && images?.length > 1}
              gap={carouselConfig?.gap || null}
              width="100%"
              height="auto"
              arrows={images?.length > 1}
              controls={images?.length > 1 ? 'dots' : null}
              loop={carouselConfig?.loopable && images?.length > 1}
            >
              {galleryImages}
            </Carousel>
          )}

          <div
            className={classes([
              'pdp-product__actions',
              [`pdp-product__actions--out-of-stock`, outOfStock],
            ])}
          >
            {outOfStock && (
              <div className="pdp-product__out-of-stock__text">
                {OutOfStockLabel}
              </div>
            )}

            {options && (
              <VComponent
                node={options}
                className={classes(['pdp-product__options'])}
              />
            )}

            {giftCardOptions && (
              <VComponent
                node={giftCardOptions}
                className={classes(['pdp-product__gift-card-options'])}
              />
            )}

            {downloadableOptions && (
              <VComponent
                node={downloadableOptions}
                className={classes(['pdp-product__downloadable-options'])}
              />
            )}

            {quantity && (
              <div className="pdp-product__quantity">
                <VComponent node={quantity} />
              </div>
            )}

            {actions && (
              <VComponent
                node={actions}
                className={classes(['pdp-product__buttons'])}
              />
            )}
          </div>

          {shortDescription && (
            <VComponent
              node={shortDescription}
              className={classes(['pdp-product__short_description'])}
            />
          )}

          {description && (
            <VComponent
              node={description}
              className={classes(['pdp-product__description'])}
            />
          )}

          {attributes ? (
            <div className="pdp-product__attributes">{attributes}</div>
          ) : null}
        </div>
        {infoContent && (
          <VComponent
            node={infoContent}
            className="pdp-product__info-content"
          />
        )}
      </div>

      {productContent && (
        <VComponent node={productContent} className="pdp-product__content" />
      )}

      {/* Zoom Overlay */}
      {zoom === null ? null : (
        <Overlay
          className="pdp-product__overlay"
          onClose={() => setZoom(null)}
          centered
        >
          <Carousel
            className="pdp-product__overlay__carousel"
            arrows={images?.length > 1}
            width="100%"
            height="100%"
            defaultIndex={zoom || 0}
            loop={carouselConfig?.loopable && images?.length > 1}
            controls={images?.length > 1 ? 'dots' : null}
            isZoomed
          >
            {galleryImages}
          </Carousel>
        </Overlay>
      )}
    </div>
  );
};
